import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Camera, X, CheckCircle2, XCircle, MessageCircle, Route, MessageSquare, SkipForward, Play, Navigation2, Package, DollarSign, Info, Bell } from "lucide-react";
import { STATE_STYLE } from "@/components/StateBadge";
import { useLiveEvents } from "@/lib/useLiveEvents";

const REASONS = [
  "Nadie en domicilio",
  "Dirección incorrecta",
  "Cliente rechazó",
  "Zona insegura",
  "Otro",
];

function CameraCapture({ onCapture, onCancel }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    let s;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then((str) => { s = str; setStream(str); if (videoRef.current) videoRef.current.srcObject = str; })
      .catch((e) => { toast.error("Cámara no disponible: " + e.message); onCancel(); });
    return () => { if (s) s.getTracks().forEach((t) => t.stop()); };
  }, []);

  const take = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    // Compress: max 900px
    const maxW = 900;
    const ratio = Math.min(1, maxW / v.videoWidth);
    canvas.width = v.videoWidth * ratio;
    canvas.height = v.videoHeight * ratio;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    if (stream) stream.getTracks().forEach((t) => t.stop());
    onCapture(dataUrl);
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="font-mono-tech text-xs uppercase tracking-widest text-yellow-300">/ Evidencia de entrega</div>
        <button onClick={onCancel} className="text-white" data-testid="cancel-photo"><X className="w-6 h-6" /></button>
      </div>
      <video ref={videoRef} autoPlay playsInline className="flex-1 object-cover w-full" />
      <div className="p-6 flex items-center justify-center">
        <button data-testid="take-photo" onClick={take} className="w-20 h-20 rounded-full border-4 border-white bg-yellow-300 flex items-center justify-center">
          <Camera className="w-8 h-8 text-black" />
        </button>
      </div>
    </div>
  );
}

export default function RutaReparto() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null); // order for photo action
  const [action, setAction] = useState(null); // "entregar" | "no_entregar"
  const [reason, setReason] = useState("");
  const [destino, setDestino] = useState("bodega");
  const [comentario, setComentario] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [ruta, setRuta] = useState(null); // ruta optimizada
  const [optimizing, setOptimizing] = useState(false);
  const [photoRequired, setPhotoRequired] = useState(true);
  const [showGroupComment, setShowGroupComment] = useState(false);
  const [groupComment, setGroupComment] = useState("");
  const [groupScope, setGroupScope] = useState("day");
  const [sendingGroup, setSendingGroup] = useState(false);
  const [session, setSession] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [startingRuta, setStartingRuta] = useState(false);

  const loadSession = () => api.get("/repartidor/ruta/session").then((r) => setSession(r.data));

  const iniciarRuta = async () => {
    setStartingRuta(true);
    try {
      const r = await api.post("/repartidor/ruta/iniciar");
      if (r.data.already) {
        toast("Ya iniciaste tu recorrido hoy");
      } else {
        toast.success(`🚚 Ruta iniciada · ${r.data.emails_queued} notificaciones enviadas`);
      }
      loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStartingRuta(false); }
  };

  const cerrarRuta = async () => {
    if (!confirm("¿Cerrar el recorrido de hoy?")) return;
    try {
      await api.post("/repartidor/ruta/cerrar");
      toast.success("Recorrido cerrado");
      loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const marcarProximo = async (order) => {
    try {
      await api.post(`/repartidor/ruta/proximo/${order.id}`);
      toast.success(`📍 Notificado a ${order.destinatario.nombre}: vas en camino`);
      if (navigator.vibrate) navigator.vibrate(100);
      loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openMaps = (o) => {
    const d = o.destinatario || {};
    const q = encodeURIComponent(`${d.direccion || ""}, ${d.comuna || ""}, Chile`);
    // Universal Google Maps URL — funciona en iOS/Android (abre la app si está instalada) y desktop
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`, "_blank");
  };

  const load = () => api.get("/orders?state=en_reparto").then((r) => setOrders(r.data));
  useEffect(() => { load(); loadSession(); }, []);

  // Live updates via WS
  useLiveEvents({
    onEvent: (evt) => {
      if (["order_assigned", "order_state_changed", "order_update", "order_en_camino"].includes(evt.type)) {
        load();
        loadSession();
      }
    },
  });

  const optimizar = async () => {
    setOptimizing(true);
    try {
      // try to get current position
      let coords = {};
      if (navigator.geolocation) {
        await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => { coords = { origin_lat: p.coords.latitude, origin_lng: p.coords.longitude }; res(); },
            () => res(),
            { timeout: 3000 }
          );
        });
      }
      const r = await api.post("/repartidor/ruta-optimizada", coords);
      setRuta(r.data);
      toast.success(`Ruta optimizada: ${r.data.count} entregas · ${r.data.total_km} km`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOptimizing(false); }
  };

  const startEntregar = async (o) => {
    setSelected(o);
    setAction("entregar");
    // Check if photo is required for this order
    try {
      const r = await api.get(`/orders/${o.id}/photo-required`);
      setPhotoRequired(r.data.required);
      if (r.data.required) {
        setShowCamera(true);
      } else {
        // Ask user: with or without photo
        setShowCamera(false);
      }
    } catch (e) { void e; setShowCamera(true); }
  };
  const startNoEntregar = (o) => { setSelected(o); setAction("no_entregar"); setReason(""); setDestino("bodega"); setComentario(""); };

  const submitEntregarSinFoto = async () => {
    try {
      await api.post(`/orders/${selected.id}/state`, {
        new_state: "entregado",
        comentario: comentario || null,
      });
      toast.success(`Entregado sin foto: ${selected.tracking_number}`);
      setSelected(null); setAction(null); setComentario("");
      load();
      loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitEntregar = async (photo) => {
    setShowCamera(false);
    try {
      await api.post(`/orders/${selected.id}/state`, {
        new_state: "entregado",
        photo_base64: photo,
        comentario: comentario || null,
      });
      toast.success(`Entregado: ${selected.tracking_number}`);
      setSelected(null); setAction(null); setComentario("");
      load();
      loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitNoEntregar = async () => {
    if (!reason) { toast.error("Selecciona un motivo"); return; }
    try {
      await api.post(`/orders/${selected.id}/state`, {
        new_state: "no_entregado",
        reason,
        destino_no_entrega: destino,
        comentario: comentario || null,
      });
      toast.success(`Marcado no entregado`);
      setSelected(null); setAction(null); setReason(""); setComentario("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitGroupComment = async () => {
    if (!groupComment.trim()) { toast.error("Escribe un comentario"); return; }
    setSendingGroup(true);
    try {
      const r = await api.post("/repartidor/ruta/comentario-grupal", { comentario: groupComment, scope: groupScope });
      toast.success(`✓ ${r.data.affected} pedidos actualizados · ${r.data.emails_queued} emails enviados a aliados`);
      setShowGroupComment(false);
      setGroupComment("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingGroup(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise pb-24">
      <header className="border-b border-white/10 bg-zinc-950/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/repartidor" className="inline-flex items-center gap-2 text-zinc-400"><ArrowLeft className="w-4 h-4" /> Volver</Link>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-yellow-300">/ Ruta del día</div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">Entregas asignadas</div>
            <div className="font-display font-black text-4xl tracking-tighter">{orders.length}</div>
          </div>
          {orders.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <button onClick={optimizar} disabled={optimizing} data-testid="btn-optimizar-ruta" className="border border-yellow-300/40 text-yellow-300 hover:bg-yellow-300/10 px-3 py-2 text-xs font-mono-tech uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
                <Route className="w-3.5 h-3.5" /> {optimizing ? "..." : "Optimizar"}
              </button>
              <button onClick={() => setShowGroupComment(true)} data-testid="btn-comentario-grupal" className="border border-white/20 hover:border-yellow-300 text-white px-3 py-2 text-xs font-mono-tech uppercase tracking-widest inline-flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Comentario grupal
              </button>
            </div>
          )}
        </div>

        {/* Session banner */}
        {orders.length > 0 && !session?.active && (
          <button onClick={iniciarRuta} disabled={startingRuta} data-testid="btn-iniciar-ruta" className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold p-5 uppercase tracking-widest flex items-center justify-between mb-5 disabled:opacity-50">
            <span className="flex items-center gap-3"><Play className="w-6 h-6" /> {startingRuta ? "Iniciando..." : "Iniciar recorrido"}</span>
            <span className="font-mono-tech text-xs">{orders.length} avisos</span>
          </button>
        )}
        {session?.active && (
          <div className="mb-5 border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="session-active">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="font-mono-tech text-[10px] text-emerald-300 uppercase tracking-widest">Recorrido en curso</span>
              </div>
              <button onClick={cerrarRuta} className="text-xs font-mono-tech text-zinc-400 hover:text-red-300 uppercase tracking-widest" data-testid="btn-cerrar-ruta">Cerrar</button>
            </div>
            <div className="text-xs text-zinc-400 font-mono-tech">
              Iniciado {new Date(session.started_at).toLocaleTimeString("es-CL", { hour: '2-digit', minute: '2-digit' })} · {session.initial_count} pedidos originales
            </div>
            {session.current_order && (
              <div className="mt-3 pt-3 border-t border-emerald-500/20">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-emerald-300 mb-1">/ En camino a</div>
                <div className="font-display font-bold text-lg">{session.current_order.destinatario?.nombre}</div>
                <div className="text-sm text-zinc-400">{session.current_order.destinatario?.direccion}</div>
              </div>
            )}
          </div>
        )}

        {ruta && ruta.count > 0 && (
          <div className="mb-6 border border-yellow-300/40 bg-yellow-300/5 p-4" data-testid="ruta-optimizada">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Ruta optimizada</div>
              <button onClick={() => setRuta(null)} className="text-zinc-400"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="font-display font-black text-2xl">{ruta.total_km} km · {ruta.count} paradas</div>
            <div className="font-mono-tech text-xs text-zinc-500 mt-1">Ordenado por vecino más cercano (haversine)</div>
            <div className="mt-3 space-y-1.5">
              {ruta.ordered.map((p, i) => (
                <div key={p.id} className="flex items-center gap-2 text-xs">
                  <span className="w-6 h-6 border border-yellow-300 text-yellow-300 flex items-center justify-center font-mono-tech font-bold">{i + 1}</span>
                  <span className="flex-1 truncate">{p.destinatario.nombre} · {p.destinatario.comuna}</span>
                  <span className="font-mono-tech text-zinc-500">{p.distance_from_prev_km} km</span>
                </div>
              ))}
              {ruta.unrouted.length > 0 && (
                <div className="text-xs text-zinc-500 font-mono-tech mt-2 pt-2 border-t border-white/10">
                  {ruta.unrouted.length} sin geocodificar (al final)
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {orders.map((o) => {
            const isCurrent = session?.current_order_id === o.id;
            return (
            <div key={o.id} className={`border p-5 ${isCurrent ? "border-yellow-300 bg-yellow-300/5" : "border-white/10 bg-zinc-900"}`} data-testid={`entrega-${o.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="font-mono-tech text-xs text-yellow-300">{o.tracking_number}</div>
                  {isCurrent && (
                    <span className="font-mono-tech text-[9px] px-2 py-0.5 bg-yellow-300 text-black uppercase tracking-widest font-bold">En camino</span>
                  )}
                </div>
                <span className={`font-mono-tech text-[9px] px-2 py-0.5 border uppercase tracking-widest ${STATE_STYLE[o.state]?.color}`}>{STATE_STYLE[o.state]?.label}</span>
              </div>
              <div className="font-display font-bold text-lg">{o.destinatario?.nombre}</div>
              <div className="flex items-start gap-2 text-sm text-zinc-400 mt-1"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-zinc-500" /><span>{o.destinatario?.direccion}, {o.destinatario?.comuna}</span></div>
              {o.destinatario?.referencia && <div className="text-xs text-zinc-500 mt-1 italic">Ref: {o.destinatario.referencia}</div>}

              {/* Primary actions row */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button onClick={() => setDetailOrder(o)} data-testid={`detalles-${o.id}`} className="border border-white/10 hover:border-yellow-300 p-2.5 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex flex-col items-center justify-center gap-0.5">
                  <Info className="w-4 h-4 text-yellow-300" /> Detalles
                </button>
                <button onClick={() => openMaps(o)} data-testid={`maps-${o.id}`} className="border border-white/10 hover:border-yellow-300 p-2.5 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex flex-col items-center justify-center gap-0.5">
                  <Navigation2 className="w-4 h-4 text-yellow-300" /> Google Maps
                </button>
                {session?.active ? (
                  <button onClick={() => marcarProximo(o)} data-testid={`proximo-${o.id}`} className="border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 p-2.5 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex flex-col items-center justify-center gap-0.5">
                    <Bell className="w-4 h-4" /> Voy hacia acá
                  </button>
                ) : (
                  o.destinatario?.telefono ? (
                    <a href={`tel:${o.destinatario.telefono}`} className="border border-white/10 hover:border-yellow-300 p-2.5 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex flex-col items-center justify-center gap-0.5" data-testid={`call-${o.id}`}>
                      <Phone className="w-4 h-4 text-yellow-300" /> Llamar
                    </a>
                  ) : <div />
                )}
              </div>

              {/* Contact secondary */}
              {session?.active && o.destinatario?.telefono && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <a href={`tel:${o.destinatario.telefono}`} className="flex-1 border border-white/10 hover:border-yellow-300 p-2 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-1.5" data-testid={`call-${o.id}`}>
                    <Phone className="w-3.5 h-3.5" /> Llamar
                  </a>
                  <a href={`https://wa.me/${(o.destinatario.telefono || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 border border-white/10 hover:border-emerald-500/40 p-2 text-center font-mono-tech text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              )}

              {/* Deliver actions */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={() => startEntregar(o)} data-testid={`entregar-${o.id}`} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Entregar
                </button>
                <button onClick={() => startNoEntregar(o)} data-testid={`no-entregar-${o.id}`} className="border border-red-500/40 text-red-300 hover:bg-red-500/10 font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-1.5">
                  <XCircle className="w-4 h-4" /> No entregar
                </button>
              </div>
            </div>
          );})}
          {orders.length === 0 && (
            <div className="text-center py-16 text-zinc-500 font-mono-tech text-sm">
              Sin entregas asignadas
            </div>
          )}
        </div>
      </main>

      {/* Detail modal */}
      {detailOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center p-4" onClick={() => setDetailOrder(null)}>
          <div className="w-full max-w-md bg-zinc-950 border border-white/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-start justify-between sticky top-0 bg-zinc-950">
              <div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Detalles del pedido</div>
                <div className="font-mono-tech text-lg font-bold mt-1">{detailOrder.tracking_number}</div>
              </div>
              <button onClick={() => setDetailOrder(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Destinatario */}
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Destinatario</div>
                <div className="font-display font-black text-2xl tracking-tighter">{detailOrder.destinatario?.nombre}</div>
                <div className="flex items-start gap-2 text-sm text-zinc-300 mt-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-yellow-300 flex-shrink-0" />
                  <span>{detailOrder.destinatario?.direccion}</span>
                </div>
                <div className="ml-6 mt-1">
                  <span className="inline-block bg-yellow-300 text-black font-bold px-2 py-0.5 text-xs uppercase tracking-widest">
                    {detailOrder.destinatario?.comuna}
                  </span>
                </div>
                {detailOrder.destinatario?.telefono && (
                  <div className="flex items-center gap-2 text-sm text-zinc-300 mt-2 font-mono-tech">
                    <Phone className="w-4 h-4 text-yellow-300" /> {detailOrder.destinatario.telefono}
                  </div>
                )}
                {detailOrder.destinatario?.referencia && (
                  <div className="mt-3 p-3 border-l-2 border-yellow-300 bg-yellow-300/5 text-sm italic">
                    &ldquo;{detailOrder.destinatario.referencia}&rdquo;
                  </div>
                )}
              </div>

              {/* Contenido */}
              <div className="border-t border-white/10 pt-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Package className="w-3 h-3" /> Contenido del paquete</div>
                <div className="text-sm text-zinc-300">{detailOrder.descripcion || "Paquete sin descripción"}</div>
                {detailOrder.valor_declarado && (
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <DollarSign className="w-4 h-4 text-yellow-300" />
                    <span className="font-mono-tech">Valor declarado: ${Math.round(detailOrder.valor_declarado).toLocaleString("es-CL")} CLP</span>
                  </div>
                )}
              </div>

              {/* Bulto */}
              {detailOrder.bulto_code && (
                <div className="border-t border-white/10 pt-4">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Parte del bulto</div>
                  <div className="font-mono-tech font-bold text-yellow-300">{detailOrder.bulto_code}</div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-2">
                <button onClick={() => { openMaps(detailOrder); }} className="bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2" data-testid="detail-maps">
                  <Navigation2 className="w-4 h-4" /> Ir con Maps
                </button>
                {detailOrder.destinatario?.telefono && (
                  <a href={`tel:${detailOrder.destinatario.telefono}`} className="border border-white/20 hover:border-yellow-300 font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4" /> Llamar
                  </a>
                )}
              </div>
              {session?.active && (
                <button onClick={() => { marcarProximo(detailOrder); setDetailOrder(null); }} className="w-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2" data-testid="detail-proximo">
                  <Bell className="w-4 h-4" /> Avisar: voy hacia acá
                </button>
              )}
              <button onClick={() => { setDetailOrder(null); startEntregar(detailOrder); }} className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold p-3 uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2" data-testid="detail-entregar">
                <CheckCircle2 className="w-4 h-4" /> Marcar entregado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera modal for entregar */}
      {showCamera && selected && (
        <CameraCapture onCapture={submitEntregar} onCancel={() => { setShowCamera(false); setSelected(null); }} />
      )}

      {/* Entregar sin foto (cuando photo_required=false) */}
      {selected && action === "entregar" && !showCamera && !photoRequired && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-950 border border-white/10">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="font-mono-tech text-xs uppercase tracking-widest text-emerald-300">/ Marcar entregado</div>
              <button onClick={() => { setSelected(null); setAction(null); }}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="font-mono-tech text-xs text-zinc-500 mb-1">Pedido</div>
                <div className="font-mono-tech">{selected.tracking_number}</div>
                <div className="text-sm mt-2">{selected.destinatario?.nombre} · {selected.destinatario?.comuna}</div>
              </div>
              <div className="border border-yellow-300/20 bg-yellow-300/5 p-3 text-xs text-zinc-300 font-mono-tech">
                Este cliente permite entregar <b className="text-yellow-300">sin foto</b>. ¿Cómo prefieres registrar?
              </div>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Comentario (opcional)</label>
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="comentario-entrega" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setShowCamera(true)} className="bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2" data-testid="entregar-con-foto">
                  <Camera className="w-4 h-4" /> Con foto
                </button>
                <button onClick={submitEntregarSinFoto} className="border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold p-3 uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2" data-testid="entregar-sin-foto">
                  <SkipForward className="w-4 h-4" /> Sin foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group comment modal */}
      {showGroupComment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-950 border border-white/10">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="font-mono-tech text-xs uppercase tracking-widest text-yellow-300">/ Comentario grupal</div>
              <button onClick={() => setShowGroupComment(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-400">
                Deja una nota que se agregará al historial de todos tus pedidos afectados. Los aliados recibirán un <b className="text-white">email de resumen</b>.
              </p>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Aplica a</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setGroupScope("day")} className={`p-3 border text-sm font-mono-tech uppercase tracking-widest ${groupScope === "day" ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10"}`} data-testid="scope-day">Todos hoy</button>
                  <button onClick={() => setGroupScope("no_entregados")} className={`p-3 border text-sm font-mono-tech uppercase tracking-widest ${groupScope === "no_entregados" ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10"}`} data-testid="scope-no-entregados">Solo no entregados</button>
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Comentario</label>
                <textarea value={groupComment} onChange={(e) => setGroupComment(e.target.value)} rows={4} placeholder="Ej: Hoy hubo manifestaciones en Providencia que retrasaron la ruta..." className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="group-comment-input" />
              </div>
              <button onClick={submitGroupComment} disabled={sendingGroup || !groupComment.trim()} className="w-full bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-sm hover:bg-yellow-200 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="submit-group-comment">
                <MessageSquare className="w-4 h-4" /> {sendingGroup ? "Enviando..." : "Enviar y notificar aliados"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No entregar modal */}
      {selected && action === "no_entregar" && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-950 border border-white/10">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="font-mono-tech text-xs uppercase tracking-widest text-red-300">/ No entregado</div>
              <button onClick={() => { setSelected(null); setAction(null); }}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="font-mono-tech text-xs text-zinc-500 mb-1">Pedido</div>
                <div className="font-mono-tech">{selected.tracking_number}</div>
              </div>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Motivo *</label>
                <div className="space-y-2">
                  {REASONS.map((r) => (
                    <label key={r} className={`block border p-3 cursor-pointer ${reason === r ? "border-yellow-300 bg-yellow-300/10" : "border-white/10 hover:border-white/30"}`}>
                      <input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} className="mr-2" data-testid={`reason-${r.substring(0,3)}`} />
                      <span className="text-sm">{r}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Destino</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setDestino("bodega")} className={`p-3 border text-sm font-mono-tech uppercase tracking-widest ${destino === "bodega" ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10"}`} data-testid="destino-bodega">Bodega</button>
                  <button onClick={() => setDestino("manana")} className={`p-3 border text-sm font-mono-tech uppercase tracking-widest ${destino === "manana" ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10"}`} data-testid="destino-manana">Reintentar mañana</button>
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Comentario (opcional)</label>
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="comentario-no-entrega" />
              </div>
              <button onClick={submitNoEntregar} disabled={!reason} className="w-full bg-red-500 hover:bg-red-400 text-white font-bold p-4 uppercase tracking-widest text-sm disabled:opacity-50" data-testid="confirm-no-entregar">
                Confirmar no entrega
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
